import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface Note {
  noteId: string;
  identifierId: string;
  identifierType: string;
  noteContent: string;
  createdDate: Date;
  createdBy: string;
  createdByName: string;
  modifiedDate?: Date;
  modifiedBy?: string;
  modifiedByName?: string;
}

export interface CreateNoteRequest {
  identifierId: string;
  identifierType: string;
  noteContent: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotesService {
  private readonly baseUrl = 'api/Notes';

  constructor(private http: HttpClient) { }

  /**
   * Get all notes for an inspection plan task
   */
  public getNotesByTaskId(inspectionPlanTaskId: string): Observable<Note[]> {
    return this.getNotesByIdentifier(inspectionPlanTaskId, 'InspectionTask');
  }

  /**
   * Get a specific note by ID
   */
  public getNoteById(id: string): Observable<Note> {
    return this.http.get<Note>(`/${this.baseUrl}/${id}`);
  }

  /**
   * Create a new note
   */
  public createNote(request: CreateNoteRequest): Observable<Note> {
    return this.http.post<Note>(`/${this.baseUrl}`, request);
  }

  /**
   * Update an existing note
   */
  public updateNote(id: string, note: Note): Observable<Note> {
    return this.http.put<Note>(`/${this.baseUrl}/${id}`, note);
  }

  /**
   * Delete a note
   */
  public deleteNote(id: string): Observable<void> {
    return this.http.delete<void>(`/${this.baseUrl}/${id}`);
  }

  /**
   * Get all notes for a specific identifier and type
   */
  public getNotesByIdentifier(identifierId: string, identifierType: string): Observable<Note[]> {
    return this.http.get<Note[]>(`/${this.baseUrl}/identifier/${identifierId}/type/${identifierType}`);
  }

  /**
   * Get count of notes for a specific identifier and type
   */
  public getNotesCountByIdentifier(identifierId: string, identifierType: string): Observable<number> {
    return this.http.get<number>(`/${this.baseUrl}/identifier/${identifierId}/type/${identifierType}/count`);
  }

  /**
   * Get count of notes for an inspection plan task
   */
  public getNotesCountByTaskId(inspectionPlanTaskId: string): Observable<number> {
    return this.getNotesCountByIdentifier(inspectionPlanTaskId, 'InspectionTask');
  }
}
